/**
 * USCoverageMap â€” choropleth of US counties colored by election authority website status.
 *
 * Each county is colored by its specific websiteStatus (when a FIPS code is present
 * in the DB). Counties without FIPS data fall back to a state-level health color.
 * Clicking a state fires onStateClick(stateCode) to filter the authority table.
 */

import { useEffect, useState } from 'react';
import { geoAlbersUsa, geoPath } from 'd3-geo';
import { feature, mesh } from 'topojson-client';

const US_ATLAS_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/counties-10m.json';

// 2-letter state abbrev â†’ 2-digit FIPS prefix
const STATE_FIPS = {
  AL: '01',
  AK: '02',
  AZ: '04',
  AR: '05',
  CA: '06',
  CO: '08',
  CT: '09',
  DE: '10',
  DC: '11',
  FL: '12',
  GA: '13',
  HI: '15',
  ID: '16',
  IL: '17',
  IN: '18',
  IA: '19',
  KS: '20',
  KY: '21',
  LA: '22',
  ME: '23',
  MD: '24',
  MA: '25',
  MI: '26',
  MN: '27',
  MS: '28',
  MO: '29',
  MT: '30',
  NE: '31',
  NV: '32',
  NH: '33',
  NJ: '34',
  NM: '35',
  NY: '36',
  NC: '37',
  ND: '38',
  OH: '39',
  OK: '40',
  OR: '41',
  PA: '42',
  RI: '44',
  SC: '45',
  SD: '46',
  TN: '47',
  TX: '48',
  UT: '49',
  VT: '50',
  VA: '51',
  WA: '53',
  WV: '54',
  WI: '55',
  WY: '56',
};
const FIPS_STATE = Object.fromEntries(Object.entries(STATE_FIPS).map(([k, v]) => [v, k]));

// Status â†’ fill color
const STATUS_FILL = {
  active: '#34d399', // emerald-400
  broken: '#f87171', // red-400
  not_found: '#fb923c', // orange-400
  unknown: '#d1d5db', // grey-300
};
const NO_DATA_FILL = '#f1f5f9'; // slate-100

// Derive a state-level background color from aggregate counts
function stateHealthFill(stats) {
  if (!stats) return NO_DATA_FILL;
  const { active = 0, broken = 0, not_found = 0, unknown = 0 } = stats;
  const total = active + broken + not_found + unknown;
  if (total === 0) return NO_DATA_FILL;
  if (broken / total > 0.15) return '#fca5a5'; // red-300
  if ((broken + not_found) / total > 0.25) return '#fdba74'; // orange-300
  if (active / total > 0.6) return '#6ee7b7'; // emerald-300
  if (active / total > 0.2) return '#fde68a'; // amber-200
  return '#e5e7eb'; // grey-200
}

const LEGEND = [
  { color: STATUS_FILL.active, label: 'Active' },
  { color: STATUS_FILL.broken, label: 'Broken' },
  { color: STATUS_FILL.not_found, label: 'Not found' },
  { color: STATUS_FILL.unknown, label: 'Unknown' },
  { color: NO_DATA_FILL, label: 'No data', border: true },
];

export default function USCoverageMap({ mapData, onStateClick, activeState }) {
  const [topology, setTopology] = useState(null);
  const [tooltip, setTooltip] = useState(null); // { x, y, content }

  useEffect(() => {
    fetch(US_ATLAS_URL)
      .then((r) => r.json())
      .then(setTopology)
      .catch(console.error);
  }, []);

  if (!topology || !mapData) {
    return (
      <div className="flex items-center justify-center h-64 text-neutral-400 text-sm">
        {!topology ? 'Loading mapâ€¦' : 'No coverage data yet.'}
      </div>
    );
  }

  const countiesGeo = feature(topology, topology.objects.counties);
  const statesMesh = mesh(topology, topology.objects.states, (a, b) => a !== b);

  const projection = geoAlbersUsa().scale(900).translate([480, 300]);

  const mapPath = geoPath(projection);

  const { byFips = {}, byState = {} } = mapData;

  function countyFill(fipsId) {
    // Exact FIPS match from DB
    if (byFips[fipsId]) return STATUS_FILL[byFips[fipsId]] ?? NO_DATA_FILL;
    // Fall back to state-level health color
    const stateFips = String(fipsId).padStart(5, '0').slice(0, 2);
    const stateCode = FIPS_STATE[stateFips];
    return stateHealthFill(stateCode ? byState[stateCode] : null);
  }

  function handleCountyHover(e, geo) {
    const fipsId = String(geo.id).padStart(5, '0');
    const stateFips = fipsId.slice(0, 2);
    const stateCode = FIPS_STATE[stateFips] ?? '??';
    const status = byFips[geo.id] ?? byFips[fipsId] ?? 'no data';
    const stats = byState[stateCode];
    const total = stats ? Object.values(stats).reduce((s, v) => s + v, 0) : 0;
    setTooltip({
      x: e.clientX + 12,
      y: e.clientY - 8,
      content: (
        <div>
          <div className="font-semibold">{stateCode}</div>
          {byFips[geo.id] || byFips[fipsId] ? (
            <div>
              County status: <span className="font-medium capitalize">{status}</span>
            </div>
          ) : (
            <div className="text-xs text-neutral-400">No county data</div>
          )}
          {stats && total > 0 && (
            <div className="text-xs mt-1 text-neutral-300">
              State: {stats.active ?? 0} active Â· {stats.broken ?? 0} broken Â· {total} total
            </div>
          )}
        </div>
      ),
    });
  }

  function handleStateClick(geo) {
    const fipsId = String(geo.id).padStart(5, '0');
    const stateFips = fipsId.slice(0, 2);
    const stateCode = FIPS_STATE[stateFips];
    if (stateCode && onStateClick) onStateClick(stateCode);
  }

  return (
    <div className="relative select-none flex flex-col h-full">
      <svg
        viewBox="0 0 960 600"
        role="img"
        aria-label="US county website coverage map"
        style={{ width: '100%', flex: '1 1 0', minHeight: 0 }}
      >
        <g>
          {countiesGeo.features.map((geo) => {
            const fipsId = String(geo.id).padStart(5, '0');
            const stateFips = fipsId.slice(0, 2);
            const stateCode = FIPS_STATE[stateFips];
            const isActive = activeState && stateCode === activeState;

            return (
              <path
                key={geo.id}
                d={mapPath(geo) || ''}
                fill={countyFill(geo.id)}
                stroke={isActive ? '#1d4ed8' : '#fff'}
                strokeWidth={isActive ? 0.6 : 0.15}
                style={{ outline: 'none', cursor: 'pointer' }}
                onMouseEnter={(e) => handleCountyHover(e, geo)}
                onMouseMove={(e) => handleCountyHover(e, geo)}
                onMouseLeave={() => setTooltip(null)}
                onClick={() => handleStateClick(geo)}
              />
            );
          })}
        </g>

        <path
          d={mapPath(statesMesh) || ''}
          fill="none"
          stroke="#475569"
          strokeWidth={0.6}
          pointerEvents="none"
        />
      </svg>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="fixed z-50 pointer-events-none bg-neutral-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl"
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          {tooltip.content}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-1 px-1 shrink-0">
        {LEGEND.map(({ color, label, border }) => (
          <div key={label} className="flex items-center gap-1.5">
            <span
              className="w-3 h-3 rounded-sm shrink-0"
              style={{ background: color, border: border ? '1px solid #d1d5db' : undefined }}
            />
            <span className="text-xs text-neutral-500">{label}</span>
          </div>
        ))}
        <span className="text-xs text-neutral-400 ml-auto">
          Click state to filter Â· Color = county status or state health
        </span>
      </div>
    </div>
  );
}
